/**
 * The capture fallback, for a browser without WebRTC Encoded Transform.
 *
 * READ THIS BEFORE TREATING ITS OUTPUT AS EVIDENCE. The primary path records the
 * exact Opus packets that crossed the network: the bytes the model heard, and
 * the bytes the provider emitted. This path records a Web Audio TAP of the
 * decoded streams and re-encodes them. For the user track the difference is
 * small — it is still post-AEC, post-noise-suppression audio, just encoded twice.
 * For the ASSISTANT track the difference is material: what we capture is this
 * particular connection's rendition of what OpenAI sent, jitter buffer, packet
 * loss concealment and all. It is a faithful record of what the human heard. It
 * is NOT a record of what the provider produced, and nothing downstream should
 * claim otherwise. `RecorderPath` travels with the session so that claim can be
 * checked rather than assumed.
 *
 * TWO TRAPS, BOTH OF WHICH PRODUCE SILENCE RATHER THAN AN ERROR:
 *
 * 1. THE REMOTE `<audio>` ELEMENT MUST EXIST AND HAVE `srcObject` SET. Chrome
 *    will not deliver audio to a `MediaStreamAudioSourceNode` built from a
 *    remote WebRTC track unless the track is also attached to a media element.
 *    transport.ts does that; do not "tidy it away" when the recorder seems to
 *    make it redundant.
 * 2. THE GRAPH MUST TERMINATE AT THE DESTINATION. See workers/pcm-tap.js.
 *
 * WHY THE CONTEXT IS PINNED TO 48 kHz. The WebCodecs Opus encoder wants 48 kHz
 * input, and pinning the context means the sample count IS the RTP tick count —
 * so the synthesised 48 kHz timeline this path reports lines up with the real
 * one from the primary path, and the server needs no special case. It also makes
 * the PCM16 downsample an exact 3:1 decimation instead of a resampler.
 */
import { PayloadKind } from '@voiceai/wire'
// The worklet SOURCE, as text, loaded from a blob — never from a URL asset.
// On the embed path Vite emitted the `?worker&url` asset path as a
// root-relative string, which resolved against the CUSTOMER'S page and 404'd,
// so the assistant track silently never recorded (session e374ff50). A blob
// URL has no origin to get wrong and no hash to go stale across a deploy —
// the same argument that inlined the recorder's dedicated workers.
import tapModuleSource from './workers/pcm-tap.js?raw'
import { loadWorkletModule } from './worklet-module'
import type { CaptureBatch, TrackName, WcAudioDataCtor, WcAudioEncoder, WcAudioEncoderCtor } from './types'

/** Packets per postMessage, matching the primary path. */
const BATCH_PACKETS = 8
/** 20 ms at 48 kHz. One Opus packet, or one PCM16 frame after decimation. */
const FRAME_SAMPLES_48K = 960
const PCM16_DECIMATION = 3

export interface FallbackOptions {
  ctx: AudioContext
  path: 'webcodecs-opus' | 'pcm16'
  onBatch(batch: CaptureBatch, transfer: ArrayBuffer[]): void
  onLog(level: 'info' | 'warn' | 'error', message: string): void
}

/** Batches packets for one track and stamps the wall-clock anchor. */
class Batcher {
  private packets: Uint8Array[] = []
  private rtp: number[] = []
  private epochMicrosFirst = 0

  constructor(
    private readonly track: TrackName,
    private readonly kind: PayloadKind,
    private readonly onBatch: FallbackOptions['onBatch'],
  ) {}

  push(packet: Uint8Array, rtp: number): void {
    if (this.packets.length === 0) {
      this.epochMicrosFirst = Math.round((performance.timeOrigin + performance.now()) * 1000)
    }
    this.packets.push(packet)
    this.rtp.push(rtp >>> 0)
    if (this.packets.length >= BATCH_PACKETS) this.flush()
  }

  flush(): void {
    if (this.packets.length === 0) return
    const batch: CaptureBatch = {
      t: 'batch',
      track: this.track,
      kind: this.kind,
      packets: this.packets,
      rtp: this.rtp,
      epochMicrosFirst: this.epochMicrosFirst,
    }
    const transfer: ArrayBuffer[] = []
    for (const p of batch.packets) transfer.push(p.buffer as ArrayBuffer)
    this.packets = []
    this.rtp = []
    this.onBatch(batch, transfer)
  }
}

interface TrackTap {
  nodes: AudioNode[]
  encoder: WcAudioEncoder | null
  batcher: Batcher
  flush(): Promise<void>
}

export class FallbackCapture {
  private taps: TrackTap[] = []
  private modulePromise: Promise<void> | null = null

  constructor(private readonly opts: FallbackOptions) {}

  /**
   * Load the worklet module. Separate from `attach` because it must happen once
   * and it is the only await in the path — doing it inside the user gesture keeps
   * the AudioContext from being auto-suspended before the first tap exists.
   */
  async prepare(): Promise<void> {
    // The PROMISE is cached, not a boolean. `addModule` throws if the same
    // processor name is registered twice, and on the hybrid path two callers race
    // for it: the recorder kicks this off inside the user gesture so the module is
    // already loading, and `attach` awaits it again when the remote track arrives.
    // A boolean flag is only set after the await, so both callers would have seen
    // false and both would have called addModule. The load itself goes through
    // the per-context cache in worklet-module.ts, because the live-audio taps a
    // listen-in adds register the same processor on the same context.
    this.modulePromise ??= loadWorkletModule(this.opts.ctx, tapModuleSource)
    await this.modulePromise
  }

  async attach(track: TrackName, stream: MediaStream): Promise<void> {
    await this.prepare()
    const { ctx, path } = this.opts
    const src = ctx.createMediaStreamSource(stream)
    const node = new AudioWorkletNode(ctx, 'pcm-tap', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      channelCount: 1,
      channelCountMode: 'explicit',
      processorOptions: { frameSamples: FRAME_SAMPLES_48K, track },
    })
    const mute = ctx.createGain()
    mute.gain.value = 0
    src.connect(node)
    node.connect(mute)
    mute.connect(ctx.destination)

    const tap: TrackTap =
      path === 'webcodecs-opus'
        ? this.opusTap(track, [src, node, mute])
        : this.pcmTap(track, [src, node, mute])
    node.port.onmessage = (ev: MessageEvent) => {
      // `Float32Array<ArrayBuffer>` rather than the bare alias: the bare one
      // widens to ArrayBufferLike, which includes SharedArrayBuffer and is
      // therefore not a `BufferSource` for the AudioData constructor.
      const msg = ev.data as { t: string; samples?: Float32Array<ArrayBuffer> }
      if (msg.t === 'pcm' && msg.samples) this.consume(tap, track, msg.samples)
    }
    this.taps.push(tap)
  }

  // Sample counters are per track and in 48 kHz ticks, which is also the RTP
  // clock. Keeping them here rather than in the tap keeps `consume` pure enough
  // to read.
  private fed: Record<TrackName, number> = { user: 0, assistant: 0 }

  /**
   * Samples that carried actual signal, per track. ORACLE 3.
   *
   * WHY THIS IS MEASURED AT ALL. This tap records CONTINUOUSLY — it is a Web Audio
   * graph, so it produces samples whether anyone is speaking or not. That makes
   * "the file is as long as the call" true of a perfect recording and equally true
   * of one that captured nothing but silence, so a length comparison is not
   * evidence. The provider's token count measures SPEECH; this is the only thing
   * comparable to it.
   *
   * WHY HERE AND NOT IN THE WORKLET. The worklet runs on the audio render thread,
   * where a missed deadline is an audible glitch in the CALL. This block has
   * already crossed to the main thread, and the samples are the same samples.
   *
   * WHY RMS AND NOT PACKET SIZES. The encoder is given a bitrate target, so packet
   * size tracks the encoder's decisions at least as much as the content. This is
   * the actual signal.
   */
  private voicedSamples: Record<TrackName, number> = { user: 0, assistant: 0 }

  /**
   * -50 dBFS. Comfortably above dither and comfortably below speech.
   *
   * HONEST ABOUT WHERE THIS IS RELIABLE: the assistant track is synthesised speech
   * with true digital silence between turns, so any sane threshold separates them
   * and this is a real measurement. A live microphone has a room behind it, so on
   * the caller's track a fixed floor will count quiet rooms as silence and noisy
   * ones as speech. That is acceptable because the caller's track does not need
   * this — it is captured wire-exact and already has an independent RTP ruler. If
   * this number is ever used for the user track, it needs a noise floor estimate
   * first, not a constant.
   */
  private static readonly VOICED_RMS = 0.00316

  /** Non-silent milliseconds captured for a track, or null if none was measured. */
  voicedMs(track: TrackName): number | null {
    if (this.fed[track] === 0) return null
    return Math.round((this.voicedSamples[track] / 48_000) * 1000)
  }

  private consume(tap: TrackTap, track: TrackName, samples: Float32Array<ArrayBuffer>): void {
    const startTick = this.fed[track]
    this.fed[track] = startTick + samples.length

    // Oracle 3, before anything is encoded or transferred. One pass, no
    // allocation, ~960 multiply-adds per 20ms block.
    let sumSquares = 0
    for (let i = 0; i < samples.length; i++) {
      const v = samples[i] ?? 0
      sumSquares += v * v
    }
    if (Math.sqrt(sumSquares / Math.max(1, samples.length)) >= FallbackCapture.VOICED_RMS) {
      this.voicedSamples[track] += samples.length
    }

    if (tap.encoder) {
      // A closed encoder is the normal end of a call, not a failure: the audio
      // graph keeps delivering render quanta for a beat after stop() closes the
      // codec, and encoding into it would throw InvalidStateError. Those frames
      // are AFTER the recording's end by definition — dropping them silently is
      // correct, and warning about them told every caller who hung up that
      // their "recording will be short".
      if (tap.encoder.state === 'closed') return
      const AudioDataCtor = (globalThis as unknown as { AudioData?: WcAudioDataCtor }).AudioData
      if (!AudioDataCtor) return
      try {
        const data = new AudioDataCtor({
          format: 'f32',
          sampleRate: 48000,
          numberOfFrames: samples.length,
          numberOfChannels: 1,
          // Microseconds, derived from the tick counter so it is exact and
          // monotone even if a render quantum is dropped.
          timestamp: Math.round((startTick / 48000) * 1_000_000),
          data: samples,
        })
        tap.encoder.encode(data)
        data.close()
      } catch (e) {
        // The state check above can still race the close by one quantum; that
        // specific throw is the same benign tail, not a shorter recording. The
        // cast is because TS narrows `state` from the check above and cannot
        // know close() flips it from under us — which is the entire race.
        if ((tap.encoder.state as string) === 'closed') return
        this.opts.onLog('warn', `opus encode failed, recording will be short: ${String(e)}`)
      }
      return
    }

    // PCM16 at 16 kHz. A box average over each group of three input samples is
    // a crude anti-alias filter, and it is deliberately crude: this path only
    // runs on a browser that has neither Encoded Transform nor WebCodecs, and a
    // proper polyphase filter here would be more code than the primary path.
    const outSamples = Math.floor(samples.length / PCM16_DECIMATION)
    const out = new Int16Array(outSamples)
    for (let i = 0; i < outSamples; i++) {
      const a = samples[i * 3] ?? 0
      const b = samples[i * 3 + 1] ?? 0
      const c = samples[i * 3 + 2] ?? 0
      const v = (a + b + c) / 3
      out[i] = Math.max(-32768, Math.min(32767, Math.round(v * 32767)))
    }
    tap.batcher.push(new Uint8Array(out.buffer, out.byteOffset, out.byteLength), startTick)
  }

  private opusTap(track: TrackName, nodes: AudioNode[]): TrackTap {
    const batcher = new Batcher(track, PayloadKind.OpusPackets, this.opts.onBatch)
    const Ctor = (globalThis as unknown as { AudioEncoder?: WcAudioEncoderCtor }).AudioEncoder
    if (!Ctor) throw new Error('no WebCodecs AudioEncoder')
    const encoder = new Ctor({
      output: (chunk) => {
        const bytes = new Uint8Array(chunk.byteLength)
        chunk.copyTo(bytes)
        // The encoder hands back the timestamp we gave it, in microseconds, so
        // the 48 kHz tick is exact rather than an estimate.
        batcher.push(bytes, Math.round((chunk.timestamp / 1_000_000) * 48000))
      },
      error: (err) => this.opts.onLog('error', `opus encoder failed: ${err.message}`),
    })
    encoder.configure({ codec: 'opus', sampleRate: 48000, numberOfChannels: 1, bitrate: 24000 })
    return { nodes, encoder, batcher, flush: async () => {
      try {
        await encoder.flush()
      } catch {
        /* a closed encoder has nothing left to give */
      }
      batcher.flush()
    } }
  }

  private pcmTap(track: TrackName, nodes: AudioNode[]): TrackTap {
    const batcher = new Batcher(track, PayloadKind.Pcm16, this.opts.onBatch)
    return { nodes, encoder: null, batcher, flush: async () => batcher.flush() }
  }

  async stop(): Promise<void> {
    for (const tap of this.taps) {
      await tap.flush()
      if (tap.encoder && tap.encoder.state !== 'closed') {
        try {
          tap.encoder.close()
        } catch {
          /* already closing */
        }
      }
      for (const n of tap.nodes) {
        try {
          n.disconnect()
        } catch {
          /* the context may already be closed */
        }
      }
    }
    this.taps = []
  }
}
